<template>
    <v-card>
        <v-card-title>{{ $t("Asetukset") }}</v-card-title>

        <v-divider></v-divider>

        <div class="settings-hub">
            <!-- Left sidebar -->
            <aside class="settings-hub__sidebar">
                <div class="settings-hub__search px-3 pt-3 pb-2">
                    <v-text-field
                        v-model="search"
                        :placeholder="$t('Etsi asetus...')"
                        :prepend-inner-icon="icons.mdiMagnify"
                        outlined
                        dense
                        hide-details
                        clearable
                    ></v-text-field>
                </div>

                <v-list dense nav class="settings-hub__list">
                    <div v-for="(group, gi) in filteredGroups" :key="`group-${gi}`">
                        <v-subheader class="settings-hub__subheader">
                            <v-icon small left>{{ group.icon }}</v-icon>
                            {{ $t(group.title) }}
                        </v-subheader>

                        <v-list-item
                            v-for="item in group.items"
                            :key="item.key"
                            :input-value="active === item.key"
                            color="primary"
                            @click="active = item.key"
                        >
                            <v-list-item-content>
                                <v-list-item-title>{{ $t(item.title) }}</v-list-item-title>
                            </v-list-item-content>
                        </v-list-item>
                    </div>

                    <v-list-item v-if="!filteredGroups.length" disabled>
                        <v-list-item-content>
                            <v-list-item-subtitle>
                                {{ $t("Ei tuloksia.") }}
                            </v-list-item-subtitle>
                        </v-list-item-content>
                    </v-list-item>
                </v-list>
            </aside>

            <!-- Right content area -->
            <section class="settings-hub__content">
                <div v-if="activeItem" class="settings-hub__content-inner">
                    <div class="d-flex align-center mb-4">
                        <v-icon class="mr-2">{{ activeItem.icon }}</v-icon>
                        <h2 class="text-h6 ma-0">{{ $t(activeItem.title) }}</h2>
                    </div>

                    <component :is="activeItem.component" />
                </div>
            </section>
        </div>
    </v-card>
</template>

<script>
import { ref, computed } from "@vue/composition-api";
import {
    mdiMagnify,
    mdiDomain,
    mdiSendOutline,
    mdiPublish,
    mdiImageOutline,
    mdiPalette,
    mdiBellOutline,
    mdiTagOutline,
    mdiShieldCheckOutline,
    mdiImageMultipleOutline,
    mdiTagMultipleOutline,
    mdiFileDocumentOutline,
} from "@mdi/js";
import { useUrlQuery } from "@/composables/useUrlQuery";

import TenantLogo from "../company-settings/components/tenant-logo.vue";
import TenantColor from "../company-settings/components/tenant-color.vue";
import FeedbackManagers from "../settings/components/feedback_managers.vue";
import FeedbackCategories from "../settings/components/feedback_categories.vue";
import FeedbackPolicy from "../settings/components/feedback_policy.vue";
import NewsImage from "../publishing-settings/components/news-image.vue";
import TagManagement from "../publishing-settings/components/tag-management.vue";
import PageCategories from "../publishing-settings/components/page-categories.vue";

export default {
    components: {
        TenantLogo,
        TenantColor,
        FeedbackManagers,
        FeedbackCategories,
        FeedbackPolicy,
        NewsImage,
        TagManagement,
        PageCategories,
    },
    setup() {
        const groups = [
            {
                title: "Yritys",
                icon: mdiDomain,
                items: [
                    { key: "logo", title: "Logo", icon: mdiImageOutline, component: "TenantLogo" },
                    { key: "color", title: "Väri", icon: mdiPalette, component: "TenantColor" },
                ],
            },
            {
                title: "Palautekanava",
                icon: mdiSendOutline,
                items: [
                    {
                        key: "feedback-managers",
                        title: "Palauteilmoitukset",
                        icon: mdiBellOutline,
                        component: "FeedbackManagers",
                    },
                    {
                        key: "feedback-categories",
                        title: "Palautteiden luokittelu",
                        icon: mdiTagOutline,
                        component: "FeedbackCategories",
                    },
                    {
                        key: "feedback-policy",
                        title: "Palautteiden käsittelykäytäntö",
                        icon: mdiShieldCheckOutline,
                        component: "FeedbackPolicy",
                    },
                ],
            },
            {
                title: "Julkaisu",
                icon: mdiPublish,
                items: [
                    {
                        key: "news-image",
                        title: "Oletusuutiskuva",
                        icon: mdiImageMultipleOutline,
                        component: "NewsImage",
                    },
                    {
                        key: "tags",
                        title: "Avainsanojen hallinta",
                        icon: mdiTagMultipleOutline,
                        component: "TagManagement",
                    },
                    {
                        key: "pages",
                        title: "Pysyvät sivut",
                        icon: mdiFileDocumentOutline,
                        component: "PageCategories",
                    },
                ],
            },
        ];

        const allItems = groups.flatMap((g) => g.items);
        const active = useUrlQuery("section", allItems[0].key);
        const search = ref("");

        const filteredGroups = computed(() => {
            const q = (search.value || "").trim().toLowerCase();
            if (!q) return groups;
            return groups
                .map((g) => ({
                    ...g,
                    items: g.items.filter(
                        (i) =>
                            i.title.toLowerCase().includes(q) ||
                            g.title.toLowerCase().includes(q),
                    ),
                }))
                .filter((g) => g.items.length > 0);
        });

        const activeItem = computed(
            () => allItems.find((i) => i.key === active.value) || allItems[0],
        );

        return {
            search,
            active,
            activeItem,
            filteredGroups,
            icons: { mdiMagnify },
        };
    },
};
</script>

<style scoped>
.settings-hub {
    display: flex;
    align-items: stretch;
    min-height: 600px;
}

.settings-hub__sidebar {
    width: 280px;
    flex: 0 0 280px;
    border-right: 1px solid rgba(0, 0, 0, 0.08);
    background-color: rgba(0, 0, 0, 0.015);
}

.settings-hub__list {
    padding: 8px;
    background: transparent;
}

.settings-hub__subheader {
    text-transform: uppercase;
    font-size: 11px;
    letter-spacing: 0.5px;
    height: 32px;
    margin-top: 8px;
}

.settings-hub__content {
    flex: 1 1 auto;
    min-width: 0;
}

.settings-hub__content-inner {
    padding: 24px;
}

@media (max-width: 768px) {
    .settings-hub {
        flex-direction: column;
    }
    .settings-hub__sidebar {
        width: 100%;
        flex: 0 0 auto;
        border-right: none;
        border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    }
}

.theme--dark .settings-hub__sidebar {
    background-color: rgba(255, 255, 255, 0.02);
    border-right-color: rgba(255, 255, 255, 0.08);
}
</style>
